import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DragOutlined,
  CheckCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import {
  useMarginRules,
  useCreateMarginRule,
  useUpdateMarginRule,
  useDeleteMarginRule
} from '../../hooks/useEstimation';

const { Option } = Select;

export const MarginRulesPage: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [form] = Form.useForm();

  // Queries
  const { data: rulesData, isLoading, refetch } = useMarginRules();
  const createRuleMutation = useCreateMarginRule();
  const updateRuleMutation = useUpdateMarginRule();
  const deleteRuleMutation = useDeleteMarginRule();

  const rules = Array.isArray(rulesData)
    ? rulesData
    : (rulesData as { data?: unknown[] })?.data ?? [];

  // Handle create/edit
  const handleSubmit = async (values: any) => {
    try {
      if (editingRule) {
        await updateRuleMutation.mutateAsync({
          id: editingRule.id,
          data: values
        });
        message.success('Margin rule updated successfully');
      } else {
        await createRuleMutation.mutateAsync(values);
        message.success('Margin rule created successfully');
      }
      setIsModalVisible(false);
      setEditingRule(null);
      form.resetFields();
      refetch();
    } catch (error) {
      message.error(`Failed to ${editingRule ? 'update' : 'create'} margin rule`);
    }
  };

  // Handle edit
  const handleEdit = (record: any) => {
    setEditingRule(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    try {
      await deleteRuleMutation.mutateAsync(id);
      message.success('Margin rule deleted successfully');
      refetch();
    } catch (error) {
      message.error('Failed to delete margin rule');
    }
  };

  // Handle toggle active
  const handleToggleActive = async (record: any) => {
    try {
      await updateRuleMutation.mutateAsync({
        id: record.id,
        data: { is_active: !record.is_active }
      });
      message.success(`Margin rule ${record.is_active ? 'deactivated' : 'activated'}`);
      refetch();
    } catch (error) {
      message.error('Failed to update margin rule');
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      sorter: (a: any, b: any) => b.priority - a.priority,
      render: (priority: number) => (
        <Tag color="blue" icon={<DragOutlined />}>
          {priority}
        </Tag>
      )
    },
    {
      title: 'Rule Name',
      dataIndex: 'rule_name',
      key: 'rule_name',
      sorter: (a: any, b: any) => a.rule_name.localeCompare(b.rule_name)
    },
    {
      title: 'Rule Type',
      dataIndex: 'rule_type',
      key: 'rule_type',
      filters: [
        { text: 'Product Category', value: 'Product Category' },
        { text: 'Customer Type', value: 'Customer Type' },
        { text: 'Order Value', value: 'Order Value' },
        { text: 'Volume', value: 'Volume' },
        { text: 'Custom', value: 'Custom' }
      ],
      onFilter: (value: any, record: any) => record.rule_type === value,
      render: (type: string) => {
        let color = 'default';
        if (type === 'Product Category') color = 'blue';
        else if (type === 'Customer Type') color = 'green';
        else if (type === 'Volume') color = 'orange';
        else if (type === 'Order Value') color = 'purple';
        return <Tag color={color}>{type}</Tag>;
      }
    },
    {
      title: 'Base Margin',
      dataIndex: 'base_margin_percentage',
      key: 'base_margin_percentage',
      render: (value: number) => `${value}%`,
      sorter: (a: any, b: any) => a.base_margin_percentage - b.base_margin_percentage
    },
    {
      title: 'Range',
      key: 'range',
      render: (_: any, record: any) => (
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          {record.min_margin_percentage}% - {record.max_margin_percentage}%
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      onFilter: (value: any, record: any) => record.is_active === value,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'} icon={isActive ? <CheckCircleOutlined /> : <StopOutlined />}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Switch
            size="small"
            checked={record.is_active}
            onChange={() => handleToggleActive(record)}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
          <Popconfirm
            title="Are you sure you want to delete this rule?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>Margin Rules Management</h1>
          <p style={{ color: '#8c8c8c', marginBottom: 0 }}>
            Configure pricing rules and margin policies
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => {
            setEditingRule(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
        >
          New Margin Rule
        </Button>
      </div>

      {/* Rules Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={isLoading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} rules`
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingRule ? 'Edit Margin Rule' : 'Create New Margin Rule'}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingRule(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={createRuleMutation.isPending || updateRuleMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="rule_name"
            label="Rule Name"
            rules={[{ required: true, message: 'Please enter rule name' }]}
          >
            <Input placeholder="Enter rule name" />
          </Form.Item>

          <Form.Item
            name="rule_type"
            label="Rule Type"
            rules={[{ required: true, message: 'Please select rule type' }]}
          >
            <Select placeholder="Select rule type">
              <Option value="Product Category">Product Category</Option>
              <Option value="Customer Type">Customer Type</Option>
              <Option value="Order Value">Order Value</Option>
              <Option value="Volume">Volume</Option>
              <Option value="Custom">Custom</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="base_margin_percentage"
            label="Base Margin (%)"
            rules={[{ required: true, message: 'Please enter base margin' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              step={0.5}
              placeholder="Enter base margin percentage"
            />
          </Form.Item>

          <Form.Item label="Margin Range">
            <Space>
              <Form.Item
                name="min_margin_percentage"
                noStyle
                rules={[{ required: true, message: 'Min required' }]}
              >
                <InputNumber
                  style={{ width: 120 }}
                  min={0}
                  max={100}
                  step={0.5}
                  placeholder="Min %"
                />
              </Form.Item>
              <span>to</span>
              <Form.Item
                name="max_margin_percentage"
                noStyle
                rules={[{ required: true, message: 'Max required' }]}
              >
                <InputNumber
                  style={{ width: 120 }}
                  min={0}
                  max={100}
                  step={0.5}
                  placeholder="Max %"
                />
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Please enter priority' }]}
            help="Higher priority rules are applied first"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={1000}
              placeholder="Enter priority (higher = more important)"
            />
          </Form.Item>

          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MarginRulesPage;

